/**
 * EmbedFundraiserModal.tsx - Fundraiser Embed Code Generation Modal
 *
 * This component generates and displays embed codes for integrating
 * fundraisers into external websites with customizable options.
 *
 * Key Features:
 * - Multiple embed code formats (iframe, widget, button)
 * - Customizable embed dimensions and styling
 * - Copy-to-clipboard functionality
 * - Preview of embedded fundraiser
 * - Responsive embed options
 * - Custom styling parameters
 *
 * Embed Options:
 * - Full fundraiser embed (complete page)
 * - Donation widget (compact form)
 * - Donation button (simple CTA)
 * - Custom dimensions and styling
 *
 * Props:
 * - isVisible: boolean - Modal visibility state
 * - onClose: () => void - Close modal handler
 * - fundraiser: Fundraiser - Fundraiser to embed
 *
 * Dependencies: MobX store, clipboard API, portal rendering
 */

import React, { useState } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import { observer } from "mobx-react-lite";
import { X, Copy, Heart } from "lucide-react";
import { fundraiserStore } from "../stores/FundraiserStore";
import { CallToAction } from "./CallToAction";
import { FundraiserCard } from "./FundraiserCard";
import {
  shouldShowExpirationBadge,
  getExpirationBadgeText,
} from "../utils/badgesUtils";
import { Toast } from "./Toast";
import { DonationCapture } from "./donation-capture/DonationCapture";
import { FundraiserEmbedViewType, FundRaiserStatus } from "../enums";
import { FundraiserDonationButtonColors } from "../models";
import { AuthService } from "@mindmemobile/mm-shared-global";
import ToastService from "./ToastService";

const ModalOverlay = styled.div<{ visible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: ${(props) => (props.visible ? "flex" : "none")};
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  backdrop-filter: blur(4px);
`;

const ModalContent = styled.div`
  background: #ffffff;
  border-radius: 24px;
  width: 90%;
  max-width: 1300px;
  height: 80vh;
  max-height: 800px;
  position: relative;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.2);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  background: rgba(0, 0, 0, 0.1);
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 10;

  &:hover {
    background: rgba(0, 0, 0, 0.2);
    color: #1e293b;
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const ModalHeader = styled.div`
  padding: 40px 40px 20px;
  border-bottom: 1px solid #f1f5f9;
  flex-shrink: 0;
`;

const ModalTitle = styled.h2`
  font-size: 28px;
  font-weight: 800;
  color: #1e293b;
  margin: 0 0 24px 0;
  text-align: center;
`;

const SegmentTabs = styled.div`
  display: flex;
  background: #f8fafc;
  border-radius: 12px;
  padding: 4px;
  gap: 4px;
`;

const SegmentTab = styled.button<{ active: boolean }>`
  flex: 1;
  padding: 12px 16px;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  ${(props) =>
    props.active
      ? `
    background: #ffffff;
    color: #1e293b;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  `
      : `
    background: transparent;
    color: #64748b;
    
    &:hover {
      color: #1e293b;
      background: rgba(255, 255, 255, 0.5);
    }
  `}
`;

const ContentContainer = styled.div`
  padding: 24px 40px 40px;
  display: grid;
  grid-template-columns: 1fr 0.8fr;
  gap: 40px;
  flex: 1;
  overflow-y: auto;
  min-height: 0;
`;

const PreviewColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const PreviewTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
`;

const PreviewContainer = styled.div`
  background: #f8fafc;
  border-radius: 16px;
  padding: 24px;
  border: 2px solid #e2e8f0;
  min-height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

// Form-specific preview container that adjusts height for scaled content
const FormPreviewContainer = styled.div`
  background: #f8fafc;
  border-radius: 16px;
  border: 2px solid #e2e8f0;
  /* Increased height to fully display DonationCapture embedded form with nice spacing */
  min-height: 1000px;
  max-height: 1000px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: visible;
  padding: 24px;
  position: relative;
`;

const CodeColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const CodeHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const CodeTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
`;

const CopyButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0;
  background: none;
  color: #64748b;
  border: none;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    color: #3b82f6;
  }

  &:active {
    color: #3b82f6;
  }
`;

const CodeTextArea = styled.textarea`
  width: 100%;
  min-height: 400px;
  padding: 16px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-family: "Monaco", "Menlo", "Ubuntu Mono", monospace;
  font-size: 14px;
  line-height: 1.5;
  color: #1e293b;
  background: #f8fafc;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const CodeCaption = styled.p`
  font-size: 14px;
  color: #64748b;
  margin: 8px 0 0 0;
  text-align: center;
`;

// Simple Donate Button Component
const SimpleDonateButton = styled.button<{ style?: React.CSSProperties }>`
  padding: 16px 46px;
  border: none;
  border-radius: 12px;
  font-size: 18px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    background: #9ca3af;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;

    &:hover {
      transform: none;
      box-shadow: none;
    }
  }
`;

// Responsive Donation Form Container for narrow preview
const ResponsiveDonationContainer = styled.div`
  width: 100%;
  max-width: 400px;

  .donation-form-container {
    padding: 24px;
  }

  /* Ensure comment label doesn't wrap */
  .comment-label-container {
    display: flex;
    align-items: center;
    flex-wrap: nowrap;
    gap: 8px;
  }

  .comment-label-text {
    white-space: nowrap;
    flex-shrink: 0;
  }

  .emoji-container {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
  }
`;

interface EmbedFundraiserModalProps {
  isVisible: boolean;
  onClose: () => void;
}

export const EmbedFundraiserModal: React.FC<EmbedFundraiserModalProps> =
  observer(({ isVisible, onClose }) => {
    const [activeSegment, setActiveSegment] = useState<FundraiserEmbedViewType>(
      FundraiserEmbedViewType.Card
    );
    const selectedFundraiser = fundraiserStore.selectedFundraiser;
    const builderData = fundraiserStore.builderData;
    const customColors = builderData?.customColors;
    const donationButtonType = builderData?.donationButtonType || "gradient";
    const donationButtonColors =
      builderData?.donationButtonColors || new FundraiserDonationButtonColors();

    // Check fundraiser conditions for button logic
    const isNotActive = selectedFundraiser?.status !== FundRaiserStatus.Active;
    const isPending =
      selectedFundraiser?.startDate &&
      new Date(selectedFundraiser.startDate) > new Date();
    const isExpiring = shouldShowExpirationBadge(
      selectedFundraiser?.endDate || null,
      selectedFundraiser?.status || FundRaiserStatus.Draft
    );

    const getDonationButtonStyles = () => {
      const background =
        donationButtonType === "solid"
          ? donationButtonColors.primary.hex
          : `linear-gradient(135deg, ${donationButtonColors.primary.hex}, ${donationButtonColors.secondary.hex})`;

      return {
        background,
        color: donationButtonColors.text?.rgba || "#ffffff",
        boxShadow: `0 4px 16px ${donationButtonColors.primary.rgba}30`,
      } as React.CSSProperties;
    };

    const getButtonText = () => {
      return "Donate";
    };

    const isButtonDisabled = () => {
      return false;
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    React.useEffect(() => {
      if (isVisible) {
        document.addEventListener("keydown", handleKeyDown);
        document.body.style.overflow = "hidden";
      } else {
        document.body.style.overflow = "unset";
      }

      return () => {
        document.removeEventListener("keydown", handleKeyDown);
        document.body.style.overflow = "unset";
      };
    }, [isVisible]);

    const handleOverlayClick = (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    };

    const getEmbedCode = (): string => {
      const baseUrl = fundraiserStore.getFundRaiserDomainBaseURL();
      const fundraiserId = selectedFundraiser?.id || "";
      const promoterId = fundraiserStore.selectedPromoterId || "";

      switch (activeSegment) {
        case "card":
          return `<iframe 
  src="${baseUrl}/fundraiser/embed/card/${fundraiserId}?PID=${promoterId || ""}" 
  width="600" 
  height="480" 
  frameborder="0" 
  scrolling="yes">
</iframe>`;

        case "cta":
          return `<iframe 
  src="${baseUrl}/fundraiser/embed/cta/${fundraiserId}?PID=${promoterId || ""}" 
  width="100%" 
  height="400" 
  frameborder="0" 
  scrolling="no">
</iframe>`;

        case "button":
          return `<iframe 
  src="${baseUrl}/fundraiser/embed/button/${fundraiserId}?PID=${promoterId || ""}" 
  width="200" 
  height="100" 
  frameborder="0" 
  scrolling="no">
</iframe>`;

        case "form":
          return `<iframe 
  src="${baseUrl}/fundraiser/embed/form/${fundraiserId}?PID=${promoterId || ""}" 
  width="100%" 
  height="1000" 
  frameborder="0" 
  scrolling="yes">
</iframe>`;

        default:
          return "";
      }
    };

    const handleCopyCode = async () => {
      const code = getEmbedCode();
      const segmentNames: Record<FundraiserEmbedViewType, string> = {
        [FundraiserEmbedViewType.Card]: "card",
        [FundraiserEmbedViewType.CTA]: "call to action",
        [FundraiserEmbedViewType.Button]: "button",
        [FundraiserEmbedViewType.Form]: "form",
      };

      try {
        await navigator.clipboard.writeText(code);
        ToastService.showSuccess(
          `Copied embed code for ${segmentNames[activeSegment]}`
        );
      } catch (err) {
        // Fallback for older browsers
        const textArea = document.createElement("textarea");
        textArea.value = code;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
        ToastService.showSuccess(
          `Copied embed code for ${segmentNames[activeSegment]}`
        );
      }
    };

    const renderPreview = () => {
      if (!selectedFundraiser) return null;

      switch (activeSegment) {
        case "card":
          return (
            <FundraiserCard
              fundraiser={selectedFundraiser}
              isEmbedPreview={true}
            />
          );

        case "cta":
          return (
            <div style={{ width: "100%", maxWidth: "380px" }}>
              <CallToAction
                customColors={customColors}
                isPreview={true}
                // isEmbedPreview={true}
              />
            </div>
          );

        case "button":
          return (
            <SimpleDonateButton
              disabled={isButtonDisabled()}
              style={getDonationButtonStyles()}
            >
              {getButtonText()}
            </SimpleDonateButton>
          );

        case "form":
          return (
            <DonationCapture
              isVisible={true}
              onClose={() => {}}
              isEmbedded={true}
              shortUniqueId={AuthService.shortUniqueId}
            />
          );

        default:
          return null;
      }
    };

    if (!isVisible || !selectedFundraiser) return null;

    return createPortal(
      <ModalOverlay visible={isVisible} onClick={handleOverlayClick}>
        <ModalContent>
          <CloseButton onClick={onClose}>
            <X size={20} />
          </CloseButton>

          <ModalHeader>
            <ModalTitle>Embed Fundraiser</ModalTitle>
            <SegmentTabs>
              <SegmentTab
                active={activeSegment === FundraiserEmbedViewType.Card}
                onClick={() => setActiveSegment(FundraiserEmbedViewType.Card)}
              >
                Card
              </SegmentTab>
              <SegmentTab
                active={activeSegment === FundraiserEmbedViewType.CTA}
                onClick={() => setActiveSegment(FundraiserEmbedViewType.CTA)}
              >
                Call to Action
              </SegmentTab>
              <SegmentTab
                active={activeSegment === FundraiserEmbedViewType.Button}
                onClick={() => setActiveSegment(FundraiserEmbedViewType.Button)}
              >
                Button
              </SegmentTab>
              <SegmentTab
                active={activeSegment === FundraiserEmbedViewType.Form}
                onClick={() => setActiveSegment(FundraiserEmbedViewType.Form)}
              >
                Form
              </SegmentTab>
            </SegmentTabs>
          </ModalHeader>

          <ContentContainer>
            <PreviewColumn>
              <PreviewTitle>Preview</PreviewTitle>
              {activeSegment === "form" ? (
                <FormPreviewContainer>{renderPreview()}</FormPreviewContainer>
              ) : (
                <PreviewContainer>{renderPreview()}</PreviewContainer>
              )}
            </PreviewColumn>

            <CodeColumn>
              <CodeHeader>
                <CodeTitle>Embed Code</CodeTitle>
                <CopyButton onClick={handleCopyCode}>
                  <Copy size={16} />
                  Copy
                </CopyButton>
              </CodeHeader>
              <CodeTextArea
                readOnly
                value={getEmbedCode()}
                placeholder="Embed code will appear here..."
              />
              <CodeCaption>Use this embed code on your website</CodeCaption>
            </CodeColumn>
          </ContentContainer>
        </ModalContent>
      </ModalOverlay>,
      document.body
    );
  });
